import React from 'react';
import Image from 'next/image';

type IndustrySolutionsProps = {
    industrySolutionsConfig: {
        icon: string;
        iconDark: string;
        title: string;
        description: string | JSX.Element;
    }[];
};

/**
 * Strips <br> elements that are direct children of a list element (<ul>/<ol>),
 * which produce invalid HTML per the spec.
 */
function stripBrFromList(element: JSX.Element): JSX.Element {
    const { type, props } = element as React.ReactElement<{ children?: React.ReactNode }>;
    if (type !== 'ul' && type !== 'ol') return element;

    const filteredChildren = React.Children.toArray(props.children).filter(
        (child) => !(React.isValidElement(child) && child.type === 'br')
    );

    return React.cloneElement(element, {}, ...filteredChildren);
}

/**
 * Wraps bare <li> content in a <ul> so the list structure is always valid.
 * If the description is already a <ul>/<ol>, we strip stray <br> siblings instead.
 *
 * The course configs author these descriptions as a Fragment of bare <li>
 * elements separated by <br> (e.g. `<><li/><br/><li/></>`). React.Children
 * does NOT descend into a fragment, so we unwrap a top-level fragment first;
 * otherwise the <br> siblings would end up as direct <ul> children and fail
 * axe's `list` audit (a <ul> may only contain <li>/script-support elements).
 */
function DescriptionWrapper({ children }: { children: React.ReactNode }) {
    // Unwrap a single top-level fragment to reach the real list items.
    let nodes: React.ReactNode = children;
    if (
        React.isValidElement(children) &&
        (children as React.ReactElement).type === React.Fragment
    ) {
        nodes = (children as React.ReactElement<{ children?: React.ReactNode }>).props.children;
    }

    const items = React.Children.toArray(nodes).filter(
        (c) => !(React.isValidElement(c) && c.type === 'br')
    );

    // Already a single <ul>/<ol>: just strip any stray <br> inside it.
    if (
        items.length === 1 &&
        React.isValidElement(items[0]) &&
        ((items[0] as React.ReactElement).type === 'ul' ||
            (items[0] as React.ReactElement).type === 'ol')
    ) {
        return <>{stripBrFromList(items[0] as JSX.Element)}</>;
    }

    // Otherwise wrap the (now <br>-free) <li> items in a valid <ul>.
    return <ul style={{ paddingLeft: '1.25rem', margin: 0 }}>{items}</ul>;
}

export function IndustrySolutions({ industrySolutionsConfig }: IndustrySolutionsProps) {
    return (
        <div className="section_industry-solutions">
            <div className="padding-global">
                <div className="solutions_component">
                    {industrySolutionsConfig.map((item, idx) => (
                        <div className="solutions_item bg-[var(--pink--50)] dark:bg-[var(--pink--800)]" key={idx}>
                            {
                                item?.icon && <div className="solutions_icon-wrapper is-large">
                                    <Image
                                            src={item.icon}
                                            unoptimized
                                            width={64}
                                            height={64}
                                            alt=""
                                            className="solutions_icon"
                                            style={{ width: "100%", height: "auto" }}
                                        />
                                    <picture>
                                        {item.iconDark && (
                                            <source srcSet={item.iconDark} media="(prefers-color-scheme: dark)" />
                                        )}
                                        <Image
                                            src={item.icon}
                                            unoptimized
                                            width={64}
                                            height={64}
                                            alt=""
                                            className="solutions_icon"
                                            style={{ width: "100%", height: "auto" }}
                                        />
                                    </picture>
                                </div>
                            }

                            <h2 className="heading-xsmall">{item.title}</h2>
                            <div className="padding-bottom padding-xsmall"></div>
                            <div className="text-rich-text w-richtext text-gray-700 dark:text-gray-100">
                                {typeof item.description === 'string' ? (
                                    item.description
                                ) : (
                                    <DescriptionWrapper>{item.description}</DescriptionWrapper>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>

    )
}
